import { FC, useEffect, useState } from 'react'

import { ArrowLeftIcon, MultiplyIcon, StarFullIcon } from '@inno-ui/icons'
import { AnimatePresence, motion } from 'motion/react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useTranslation } from 'react-i18next'
import { ONBOARDING_PINNED_APP_ID } from 'shared/hooks/useOnboarding'
import { AppConfig } from 'shared/types/app'

import { useLaunchpadStore } from '../../../../../stores/launchpadStore'
import { transformAppConfigToAppData } from '../../AppButton/utils/appDataTransform'
import {
  ALL_APPS_VIEW_VARIANTS,
  BACK_TOGGLE_VARIANTS,
  SPOTLIGHT_VIEW_VARIANTS,
  VIEW_TRANSITION
} from '../LaunchPad.animations'
import { LaunchPadProps } from '../LaunchPad.types'
import { getTypedGroupEntries } from '../LaunchPad.utils'
import { AppIconRenderer, GroupSection, SpotlightHero } from '../components'
import { useLaunchPadApps } from '../hooks/useLaunchPadApps'
import styles from './LaunchPadDesktop.module.scss'

export const LaunchPadDesktop: FC<LaunchPadProps> = ({
  handleAppClick,
  iconRefs,
  onAnimationComplete,
  inventoryAppContextMenuRef
}) => {
  const isOpen = useLaunchpadStore(state => state.isOpen)
  const setIsLaunchpadOpen = useLaunchpadStore(
    state => state.setIsLaunchpadOpen
  )

  const { appsByGroup, isAppLocked, isLoading, spotlightApps } =
    useLaunchPadApps()
  const { t } = useTranslation('translation', {
    keyPrefix: 'launchpad.spotlight'
  })
  const [isShowingAllApps, setIsShowingAllApps] = useState(false)
  const isSpotlightView = !isShowingAllApps && spotlightApps.length > 0

  useEffect(() => {
    if (!isOpen) setIsShowingAllApps(false)
  }, [isOpen])

  useHotkeys('escape', () => setIsLaunchpadOpen(false), {
    enabled: isOpen
  })

  const handleAppClickInternal = (
    appData: Parameters<NonNullable<LaunchPadProps['handleAppClick']>>[0]
  ) => {
    handleAppClick?.(appData, 'launchpad')
  }

  const handleSpotlightAppClick = (app: AppConfig) => {
    if (isAppLocked(app)) return
    setIsLaunchpadOpen(false)
    handleAppClickInternal(transformAppConfigToAppData(app))
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={styles.overlay}
          onAnimationComplete={onAnimationComplete}
          onClick={e => {
            e.stopPropagation()
            setIsLaunchpadOpen(false)
          }}
        >
          <motion.div
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            exit={{ scale: 1.2 }}
            className={styles.modal}
          >
            <div onClick={() => setIsLaunchpadOpen(false)}>
              <MultiplyIcon className={styles.closeIcon} />
            </div>
            <AnimatePresence>
              {!isSpotlightView && spotlightApps.length > 0 && (
                <motion.div
                  key='back-toggle'
                  className={styles.spotlightToggle}
                  variants={BACK_TOGGLE_VARIANTS}
                  initial='hidden'
                  animate='visible'
                  exit='exit'
                  transition={VIEW_TRANSITION}
                >
                  <button
                    type='button'
                    className={styles.backToSpotlight}
                    onClick={e => {
                      e.stopPropagation()
                      setIsShowingAllApps(false)
                    }}
                  >
                    <ArrowLeftIcon />
                    <StarFullIcon className={styles.starIcon} />
                    {t('back_to_spotlight')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            <div className={styles.modalContent}>
              <AnimatePresence mode='wait' initial={false}>
                {isSpotlightView ? (
                  <motion.div
                    key='spotlight-view'
                    variants={SPOTLIGHT_VIEW_VARIANTS}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    transition={VIEW_TRANSITION}
                  >
                    <SpotlightHero
                      apps={spotlightApps}
                      variant='light'
                      isAppLocked={isAppLocked}
                      onAppClick={handleSpotlightAppClick}
                      onShowAllApps={() => setIsShowingAllApps(true)}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key='all-apps-view'
                    className={styles.launchpadContent}
                    variants={ALL_APPS_VIEW_VARIANTS}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    transition={VIEW_TRANSITION}
                  >
                    {appsByGroup.map(groupData =>
                      getTypedGroupEntries(groupData).map(
                        ([groupKey, apps]) => (
                          <GroupSection
                            key={groupKey}
                            groupKey={groupKey}
                            apps={apps || []}
                            styles={{
                              groupSection: styles.groupSection,
                              groupHeader: styles.groupHeader,
                              groupTitle: styles.groupTitle,
                              groupProLabel: styles.groupProLabel,
                              appsGrid: styles.appsGrid
                            }}
                          >
                            {apps?.map((app, i) => (
                              <AppIconRenderer
                                key={app.id}
                                app={app}
                                index={i}
                                isLocked={isAppLocked(app)}
                                isLoading={isLoading}
                                buttonType='launchpad'
                                size='large'
                                variant='light'
                                iconRefs={iconRefs}
                                contextMenuRef={
                                  app.id === ONBOARDING_PINNED_APP_ID
                                    ? inventoryAppContextMenuRef
                                    : undefined
                                }
                                onAppClick={handleAppClickInternal}
                                onCloseLaunchpad={() =>
                                  setIsLaunchpadOpen(false)
                                }
                              />
                            ))}
                          </GroupSection>
                        )
                      )
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
